import {
  type capitalizeFirstLetter,
  type fISOToReadableNoTimeShort,
} from '@autone/utils';
import {
  Box,
  Divider,
  Grid,
  Stack,
  type SxProps,
  Typography,
  type TypographyProps,
} from '@mui/material';
import React from 'react';

import { DATA_MISSING_TEXT } from './constants';

const METRIC_WIDTH = '100px';

export const Metric = ({
  children,
  sx,
  color,
}: {
  children: React.ReactNode;
  sx?: SxProps;
  color?: TypographyProps['color'];
}) => (
  <Typography
    sx={{
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      ...sx,
    }}
    color={color}
  >
    {children}
  </Typography>
);

export const MetricHeaderAndValue = ({
  metric,
  missingDataText,
  width,
  metricSx,
}: {
  metric: {
    title: string;
    value?: string | null;
    formatter?: typeof capitalizeFirstLetter | typeof fISOToReadableNoTimeShort;
  };
  missingDataText?: string;
  width: string;
  metricSx?: SxProps;
}) => {
  return (
    <Box>
      <Metric sx={{ fontWeight: 500, width: width, ...metricSx }}>
        {metric.title}
      </Metric>
      {metric.value == null || metric.value === 'UNDEFINED' ? (
        <Metric sx={{ width: width, ...metricSx }} color={'error.dark'}>
          {missingDataText}
        </Metric>
      ) : (
        <Metric sx={{ width: width, ...metricSx }} color={'text.secondary'}>
          {metric.formatter ? metric.formatter(metric.value) : metric.value}
        </Metric>
      )}
    </Box>
  );
};

export const MetricGroup = ({
  metrics,
  missingDataText = DATA_MISSING_TEXT,
  gap = 2,
  metricWidth = METRIC_WIDTH,
  metricSx,
}: {
  metrics: {
    title: string;
    value?: string | null;
    formatter?: typeof capitalizeFirstLetter | typeof fISOToReadableNoTimeShort;
  }[];
  missingDataText?: string;
  gap?: number;
  metricWidth?: string;
  metricSx?: SxProps;
}) => {
  return (
    <Grid container columns={2} gap={gap}>
      {metrics.map((metric, index) => (
        <React.Fragment key={index}>
          <Grid item key={index}>
            <Stack direction="column" alignItems="center">
              <MetricHeaderAndValue
                missingDataText={missingDataText}
                metric={metric}
                metricSx={metricSx}
                width={metricWidth}
              />
            </Stack>
          </Grid>
          {index % 2 === 0 && <Divider orientation="vertical" flexItem />}
        </React.Fragment>
      ))}
    </Grid>
  );
};
